// Load one thing from the Worker when a route is displayed.
//
// This hook has no yominoki original and could not have: every page was an
// `async function Page()` that awaited its data before any HTML existed. A
// client-rendered route has to ask for it after it mounts, which puts the four
// pages in exactly the situation §3.2 of the effect policy is about — so the
// race handling lives here once rather than being re-derived four times.
//
// Two mechanisms, because they cover different failures. The AbortController
// stops the request itself when the route unmounts; the `ignore` flag stops a
// response that has already been received from calling setState on a route the
// reader has left, which an abort cannot prevent once the body has landed.
//
// `deps` is the identity of what is being loaded — a reading id, a map number
// — and is what re-runs the load. Passing the loader itself would re-run on
// every render unless every caller wrapped it in useCallback, so the loader is
// read through a ref and deliberately kept out of the dependency array.

import { useCallback, useEffect, useRef, useState } from "react";

export type Resource<T> =
  | { status: "loading" }
  | { status: "ready"; data: T }
  | { status: "error"; error: unknown };

export function useResource<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
): { resource: Resource<T>; reload: () => void } {
  const [resource, setResource] = useState<Resource<T>>({ status: "loading" });
  const [nonce, setNonce] = useState(0);

  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  });

  useEffect(() => {
    let ignore = false;
    const controller = new AbortController();
    // oxlint-disable-next-line react/set-state-in-effect -- deliberately violates react-useeffect-discipline §1.3: on a deps/nonce change this commits one frame of the previous resource (stale data or a stale error) before this fires and flips it to loading. Follow-up: kanban #191.
    setResource({ status: "loading" });
    loadRef.current(controller.signal).then(
      (data) => {
        if (!ignore) setResource({ status: "ready", data });
      },
      (error: unknown) => {
        if (!ignore) setResource({ status: "error", error });
      },
    );
    return () => {
      ignore = true;
      controller.abort();
    };
    // `deps` IS the dependency list; `load` is read through a ref on purpose (see the header).
  }, [...deps, nonce]);

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  return { resource, reload };
}
